import React, { useContext, useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext.js";
import VideoThumb from "../components/VideoThumb.js";
import "./styles/CategoryPage.css";

const categories = ['politics', 'world', 'business', 'health', 'technology', 'entertainment', 'sports', 'local'];
const categoriesWithSubs = ['entertainment', 'sports', 'business', 'technology'];

const CategoryPage = () => {
  const { category, subcategory: subFromUrl } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [allSubcategories, setAllSubcategories] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState("");

  useEffect(() => {
    if (category === 'local') {
      fetch('/api/regions')
        .then(res => res.json())
        .then(data => {
          setRegions(data);
          if (subFromUrl && data.find(r => r.slug === subFromUrl)) {
            setSelectedRegion(subFromUrl);
            localStorage.setItem('vyral_preferred_region', subFromUrl);
          } else if (!subFromUrl) {
            const saved = localStorage.getItem('vyral_preferred_region');
            if (saved && data.find(r => r.slug === saved)) {
              setSelectedRegion(saved);
            }
          }
        })
        .catch(err => console.error("Error fetching regions:", err));
    } else {
      setRegions([]);
      setSelectedRegion("");
    }
  }, [category, subFromUrl]);

  useEffect(() => {
    setSubcategories([]);

    const fetchArticles = async () => {
      try {
        let url = `/api/articles?category=${category}`;
        if (category === 'local' && selectedRegion) {
          url += `&region=${selectedRegion}`;
        } else if (subFromUrl) {
          url += `&subcategory=${subFromUrl}`;
        }
        const resp = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        });
        if (!resp.ok) throw new Error(`Failed to load articles: ${resp.status}`);
        const data = await resp.json();
        setArticles(data);
      } catch (err) {
        console.error("Error fetching articles:", err);
        setError(err.message);
      }
    };
    fetchArticles();

    if (categoriesWithSubs.includes(category)) {
      fetch(`/api/subcategories?category=${category}`)
        .then(res => res.json())
        .then(data => setSubcategories(data))
        .catch(err => console.error("Error fetching subcategories:", err));
    }

    const fetchAllSubcategories = async () => {
      const subcats = {};
      for (const cat of categoriesWithSubs) {
        try {
          const res = await fetch(`/api/subcategories?category=${cat}`);
          const data = await res.json();
          subcats[cat] = data;
        } catch (err) {
          subcats[cat] = [];
        }
      }
      setAllSubcategories(subcats);
    };
    fetchAllSubcategories();
  }, [token, category, subFromUrl, selectedRegion]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRegionChange = (slug) => {
    setSelectedRegion(slug);
    if (slug) {
      localStorage.setItem('vyral_preferred_region', slug);
      navigate(`/category/local/${slug}`);
    } else {
      localStorage.removeItem('vyral_preferred_region');
      navigate('/category/local');
    }
  };

  const filteredArticles = articles;

  const getTypeLabel = (type) => {
    const labels = {
      politics: 'Politics',
      entertainment: 'Entertainment',
      sports: 'Sports',
      local: 'Local',
      business: 'Business',
      technology: 'Technology',
      health: 'Health',
      world: 'World'
    };
    return labels[type] || type;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getExcerpt = (article) => {
    const text = article.excerpt || (article.content ? article.content.substring(0, 120) : "");
    if (text.length > 150) return text.substring(0, 150) + "...";
    return text;
  };

  const selectedRegionObj = regions.find(r => r.slug === selectedRegion);

  return (
    <div className="category-page">
      <nav className="content-bar" ref={dropdownRef}>
        <div className="content-bar-inner">
          {categories.map(cat => (
            <div key={cat} className="category-item">
              {categoriesWithSubs.includes(cat) ? (
                <button
                  type="button"
                  className={`content-bar-link has-dropdown ${cat === category ? 'active' : ''}`}
                  onClick={() => setOpenDropdown(openDropdown === cat ? null : cat)}
                >
                  {getTypeLabel(cat)}
                  <span className="dropdown-arrow">▼</span>
                </button>
              ) : (
                <Link 
                  to={`/category/${cat}`}
                  className={`content-bar-link ${cat === category ? 'active' : ''}`}
                >
                  {getTypeLabel(cat)}
                </Link>
              )}
              {categoriesWithSubs.includes(cat) && (
                <div className={`subcategory-dropdown ${openDropdown === cat ? 'show' : ''}`}>
                  <Link
                    to={`/category/${cat}`}
                    className="dropdown-item"
                    onClick={() => setOpenDropdown(null)}
                  >
                    All {getTypeLabel(cat)}
                  </Link>
                  {(allSubcategories[cat] || []).map(sub => (
                    <Link
                      key={sub.slug}
                      to={`/category/${cat}/${sub.slug}`}
                      className="dropdown-item"
                      onClick={() => setOpenDropdown(null)}
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </nav>

      <header className="category-header">
        <h1>
          {getTypeLabel(category)}
          {category === 'local' && selectedRegionObj && (
            <span className="region-subtitle"> — {selectedRegionObj.name}, {selectedRegionObj.state}</span>
          )}
        </h1>
        <p>{filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''}</p>
      </header>

      {category === 'local' && regions.length > 0 && (
        <div className="region-selector">
          <div className="region-selector-inner">
            <span className="region-label">Your Region:</span>
            <select
              value={selectedRegion}
              onChange={(e) => handleRegionChange(e.target.value)}
              className="region-select"
            >
              <option value="">All Regions</option>
              {regions.map(reg => (
                <option key={reg.id} value={reg.slug}>
                  {reg.name}, {reg.state}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {error && <p className="error">Error: {error}</p>}

      <div className="category-articles-grid">
        {filteredArticles.map(article => (
          <Link key={article.id} to={`/article/${article.id}`} className="category-article-card article-card-link">
            {article.image_url && (
              article.media_type === 'video' ? (
                article.cover_image_url ? (
                  <div className="card-video-thumb category-article-image card-has-cover">
                    <img src={article.cover_image_url} alt={article.title} className="card-cover-img" />
                    <span className="video-badge card-video-badge">VIDEO</span>
                  </div>
                ) : (
                  <VideoThumb
                    src={article.image_url}
                    startTime={article.video_start_time}
                    alt={article.title}
                    imgClassName="card-cover-img"
                    wrapperClassName="card-video-thumb category-article-image card-has-cover"
                    showOverlay={false}
                  />
                )
              ) : (
                <img className="category-article-image" src={article.image_url} alt={article.title} />
              )
            )}
            <div className="category-article-content">
              {article.subcategory_slug && (
                <span className="subcategory-tag">
                  {subcategories.find(s => s.slug === article.subcategory_slug)?.label || article.subcategory_slug.toUpperCase()}
                </span>
              )}
              {article.region_name && (
                <span className="region-tag">
                  {article.region_name}
                </span>
              )}
              <h2 className="category-article-title">
                {article.title}
                {article.is_premium && <span className="premium-tag-small">PREMIUM</span>}
              </h2>
              <p className="category-article-excerpt">{getExcerpt(article)}</p>
              <div className="category-article-meta">
                <span>By {article.author_name}</span>
                <span>{formatDate(article.created_at)}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {filteredArticles.length === 0 && !error && (
        <p className="no-articles">
          {category === 'local' && selectedRegion
            ? `No local news found for ${selectedRegionObj?.name || 'this region'}. Check back soon or try a different region.`
            : 'No articles found in this category.'}
        </p>
      )}
    </div>
  );
};

export default CategoryPage;
